import { useState } from 'react';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { CUSTOM_OPTION } from '@/modules/analytics/periodOptions';

import DateFilterItem from './DateFilterItem/DateFilterItem';
import { DateRangeCalendar } from './DateRangeCalendar';
import { type DateRangeLayoutProps } from './DateRangeMenu';
import { DateRangePresetList } from './DateRangePresetList';
import { useDateRangeDraft } from './useDateRangeDraft';

export const DateRangeCompactDropdown = ({
  ref: calendarRef,
  startDate,
  endDate,
  selectedPeriod,
  onPresetSelect,
  onRangeSelect,
  isPresetDisabled,
  isDayDisabled,
  retentionStart,
  today,
  align = 'start',
  footer,
}: DateRangeLayoutProps & { ref?: React.Ref<HTMLDivElement> }) => {
  const [calendarOpen, setCalendarOpen] = useState(false);

  const { shown, isDrafting, handleSelect, clearDraft } = useDateRangeDraft(
    startDate,
    endDate,
    onRangeSelect,
    () => setCalendarOpen(false)
  );

  return (
    <div
      className={cn(
        'absolute top-full left-0 z-30 mt-1 flex w-fit min-w-48 flex-col items-start gap-px rounded-lg border border-stroke bg-background p-1 shadow-md',
        align === 'end' && 'right-0 left-auto'
      )}
    >
      <DateRangePresetList
        selected={isDrafting ? null : selectedPeriod}
        onSelect={onPresetSelect}
        isDisabled={isPresetDisabled}
        variant="check"
        className="gap-px"
      />

      <Popover
        open={calendarOpen}
        onOpenChange={(open) => {
          if (open) {
            clearDraft();
          }
          setCalendarOpen(open);
        }}
      >
        <PopoverTrigger asChild>
          <div className="w-full">
            <DateFilterItem item={CUSTOM_OPTION} selected={!selectedPeriod} variant="check" />
          </div>
        </PopoverTrigger>
        <PopoverContent ref={calendarRef} className="w-auto p-0" align="start" sideOffset={8}>
          <DateRangeCalendar
            selected={shown}
            onSelect={handleSelect}
            isDayDisabled={isDayDisabled}
            startMonth={retentionStart}
            endMonth={today}
            numberOfMonths={1}
          />
        </PopoverContent>
      </Popover>

      {footer}
    </div>
  );
};
